/*
   Evaluation runs, as the console reads them: a measurement of the review against a client's
   expert-marked labels. Mirrors schemas/eval_runs.py on the backend (adapted to camelCase in
   api/eval-runs.ts). Coordinates are PDF points with a top-left origin; pages are 0-based.
*/

import type { DetectorProfileResolved } from "@/components/domain/detector-profile"

import type { DatasetProfile } from "./dataset-profile"

export type EvalRunId = number & { readonly __brand: "EvalRunId" }

/** What the agreed (judged) view made of an expert mark. */
export type MarkOutcome = "caught" | "escalated" | "missed" | "not_checked" | "out_of_rules"
/** How a mark was paired with a finding: its box, quoted text, same page, or whole document. */
export type MarkHow = "loc" | "text" | "page" | "doc"
export type PointBox = readonly [number, number, number, number]
export type FindingOutput = "flag" | "no_flag" | "needs_sme"
export type RemarkTargetKind = "mark" | "finding" | "label"
/**
 * Whether a mark can be caught by reviewing the label against its master. Anything else is
 * excluded from the in-scope figures: an ask about the master's own text, one that needs facts
 * neither document holds, or advice that requests no change. Runs from before the tag carry none,
 * and a mark without one is in scope.
 */
export type MarkScope = "in_scope" | "master_text" | "outside_documents" | "advisory"
export type OutOfScope = Exclude<MarkScope, "in_scope">
/** Who settled a mark's scope: the classifier, an admin overriding it, or nobody (in scope). */
export type ScopeSource = "ai" | "admin" | "default"

export interface EvalMark {
  id: string
  page: number
  rect: PointBox | null
  comment: string
  highlighted: string
  rules: readonly string[]
  outcome: MarkOutcome
  how: MarkHow | null
  findingId: number | null
  rejectedByTiebreak: boolean
  tiebreakReason: string | null
  scope: MarkScope
  scopeReason: string | null
  scopeSource: ScopeSource
  /** The classifier's own call, kept when an admin overrides it. */
  aiScope: MarkScope | null
  aiScopeReason: string | null
  scopeSetByName: string | null
  scopeSetAt: string | null
}

export interface EvalFinding {
  id: number
  ruleId: string
  output: FindingOutput
  message: string
  divergence: string | null
  page: number | null
  boxes: readonly { page: number; bbox: PointBox }[]
}

export interface LabelCounts {
  caught: number
  escalated: number
  missed: number
  notChecked: number
  flags: number
  handoffs: number
}

/** Where an admin has got to with a run. Runs are staff-only in every state. */
export type EvalRunStatus = "draft" | "reviewed" | "archived"

export const EVAL_RUN_STATUSES: readonly EvalRunStatus[] = ["draft", "reviewed", "archived"]

export const EVAL_RUN_STATUS_LABEL: Readonly<Record<EvalRunStatus, string>> = {
  draft: "Draft",
  reviewed: "Reviewed",
  archived: "Archived",
}

export interface EvalRun {
  id: EvalRunId
  name: string
  dataset: string
  method: string
  notes: string | null
  summary: Readonly<Record<string, number | null>>
  serviceRevision: string | null
  status: EvalRunStatus
  createdAt: string
  labelCount: number
  /** The detector settings the run's reviews applied. Null when the run did not record them. */
  detectorProfile: DetectorProfileResolved | null
  /** The derived profile file the run used, with its evidence. Null when it used none. */
  datasetProfile: DatasetProfile | null
  /** How the run's marks were tagged (classifier and model); null when they were not. */
  markScopeMethod: string | null
  /** Summary keys recomputed after admin scope decisions; empty when none were. */
  summaryRecomputed: readonly string[]
  /** How many of this run's marks an admin decided the scope of; null when the server does not say. */
  scopeDecisions: number | null
}

/**
 * How a label's master was mapped. `pyxis`: the client's own mapping (or the master it supplied).
 * `auto`: we resolved the registration and downloaded the master from EPA PPLS. `auto_fixed`: we
 * overrode a serious client mismapping, with the evidence on each fix.
 */
export type MasterMappingKind = "pyxis" | "auto" | "auto_fixed"
export type MasterSource = "ppls" | "client"

export interface EvalMasterFix {
  what: string
  why: string
  evidence: readonly string[]
  serious: boolean
  date: string | null
}

/** The master a label was compared against, and how it was chosen. */
export interface EvalMasterMapping {
  kind: MasterMappingKind
  source: MasterSource
  rule: string
  masterFile: string
  masterDate: string | null
  supplements: readonly string[]
  regNr: string | null
  regSource: string | null
  fixes: readonly EvalMasterFix[]
}

export const MASTER_KIND_LABEL: Record<MasterMappingKind, string> = {
  pyxis: "The client's mapping",
  auto: "Resolved by us",
  auto_fixed: "The client's mapping, corrected by us",
}

export const MASTER_SOURCE_LABEL: Record<MasterSource, string> = {
  ppls: "EPA PPLS",
  client: "Supplied by the client",
}

export interface EvalLabelRow {
  mid: string
  product: string
  kind: string
  fileName: string
  hasPdf: boolean
  /** Whether the run uploaded the label's master PDF. False on servers that predate it. */
  hasMasterPdf: boolean
  /** The master the label was compared against; null when the run did not record it. */
  master: EvalMasterMapping | null
  counts: LabelCounts
  remarkCount: number
}

export interface EvalLabel extends EvalLabelRow {
  marks: readonly EvalMark[]
  findings: readonly EvalFinding[]
  /** Label page → the master page carrying its text, both 0-based. Empty on runs without one. */
  pageMap: ReadonlyMap<number, number>
}

export interface Remark {
  id: number
  targetKind: RemarkTargetKind
  targetRef: string
  authorRole: string
  authorName: string | null
  body: string
  createdAt: string
}

export const OUTCOME_LABEL: Record<MarkOutcome, string> = {
  caught: "Caught",
  escalated: "Escalated to a reviewer",
  missed: "Missed",
  not_checked: "Rule not checked",
  out_of_rules: "Advice, outside the rules",
}

/**
 * Tone per outcome, from the colour tokens: settled is master, in progress is print, an error is
 * err, and anything without a verdict stays graphite. A finding of ours is info.
 */
export const OUTCOME_TONE: Record<MarkOutcome, string> = {
  caught: "var(--master)",
  escalated: "var(--print)",
  missed: "var(--err)",
  not_checked: "var(--text-3)",
  out_of_rules: "var(--text-3)",
}
export const FINDING_TONE = "var(--info)"

export const OUT_OF_SCOPE: readonly OutOfScope[] = ["master_text", "outside_documents", "advisory"]

export const MARK_SCOPES: readonly MarkScope[] = ["in_scope", ...OUT_OF_SCOPE]

export const SCOPE_LABEL: Record<MarkScope, string> = {
  in_scope: "In scope",
  master_text: "Master text",
  outside_documents: "Outside documents",
  advisory: "Advisory",
}

export const HOW_LABEL: Record<MarkHow, string> = {
  loc: "our finding sits on the mark",
  text: "our finding quotes the marked text",
  page: "same page, confirmed by the tie-break",
  doc: "a whole-document finding, confirmed by the tie-break",
}

/** How a figure reads: a share as a percentage, an average to two decimals, a count whole. */
export type FigureFormat = "percent" | "decimal" | "count"

/** The run summary's keys, as they read on screen, with the target each is held to. */
export const SUMMARY_ROWS: readonly {
  key: string
  label: string
  target?: string
  format: FigureFormat
}[] = [
  {
    key: "safety_recall",
    label: "Safety recall (caught or escalated)",
    target: "≥ 90%",
    format: "percent",
  },
  { key: "recall_flagged", label: "Recall, flagged only", format: "percent" },
  {
    key: "handoffs_per_commented_label",
    label: "Hand-offs per commented label",
    target: "< 3.00",
    format: "decimal",
  },
  { key: "flags_per_label", label: "Flags per label", format: "decimal" },
  { key: "marks_scored", label: "Expert marks scored", format: "count" },
  { key: "labels", label: "Labels", format: "count" },
]

export function formatFigure(value: number | null | undefined, format: FigureFormat): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—"
  if (format === "percent") return `${(value * 100).toFixed(1)}%`
  if (format === "decimal") return value.toFixed(2)
  return Math.round(value).toLocaleString()
}

/** Whether a figure meets its target; null where the figure has no target or no value. */
export function meetsTarget(key: string, value: number | null | undefined): boolean | null {
  if (value === null || value === undefined) return null
  if (key === "safety_recall") return value >= 0.9
  if (key === "handoffs_per_commented_label") return value < 3
  return null
}

/** The neighbours of a label in the run's own order, for previous/next navigation. */
export function neighbours(
  order: readonly string[],
  mid: string,
): { prev: string | null; next: string | null; position: number } {
  const at = order.indexOf(mid)
  return {
    prev: at > 0 ? (order[at - 1] ?? null) : null,
    next: at >= 0 && at + 1 < order.length ? (order[at + 1] ?? null) : null,
    position: at,
  }
}
